import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPencilOutline, mdiHelpCircleOutline } from "@mdi/js";
import MDEditor, { commands } from "@uiw/react-md-editor";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  fetchDisclaimer,
  saveDisclaimer,
  clearSaveStatus,
  toggleDisclaimerVisibility,
  clearVisibilityStatus,
} from "./slice";
import DisclaimerHowItWorksModal from "./DisclaimerHowItWorksModal";
import HideFromReportsModal from "./HideFromReportsModal";
import DiscardChangesModal from "app/components/common/DiscardChangesModal";
import EmptyContentModal from "./EmptyContentModal";
import CustomToast from "../../components/common/CustomToast";
import { PartialLoadingSpinner } from "../../components/LoadingSpinner";
import PageLayout from "../../components/common/PageLayout";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "../../hooks/useInitialFetch";
import useScrollSync from "./useScrollSync";
import "./disclaimerSettings.css";

const DisclaimerSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    device: { isMobile },
  } = useApp();

  const {
    content,
    isHiddenFromReports,
    fetchStatus,
    saveStatus,
    fetchError,
    saveError,
    visibilityStatus,
    visibilityError,
  } = useSelector((state) => state.disclaimer);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showHideFromReportsModal, setShowHideFromReportsModal] =
    useState(false);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showEmptyContentModal, setShowEmptyContentModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const isEditModeRef = useRef(false);
  const editContentRef = useRef("");
  const originalContentRef = useRef("");
  const editorWrapperRef = useRef(null);

  // Synchronized scrolling between editor and preview (desktop only)
  useScrollSync(editorWrapperRef, isEditMode && !isMobile);

  // Fetch disclaimer on mount
  useInitialFetch(() => dispatch(fetchDisclaimer()));

  // Handle save success
  useEffect(() => {
    if (saveStatus === "success") {
      setIsEditMode(false);
      isEditModeRef.current = false;
      editContentRef.current = "";
      originalContentRef.current = "";
      setToastMessage("Disclaimer saved successfully");
      setShowSuccessToast(true);
      dispatch(clearSaveStatus());
    } else if (saveStatus === "failed") {
      setToastMessage(saveError || "Failed to save disclaimer");
      setShowErrorToast(true);
      dispatch(clearSaveStatus());
    }
  }, [saveStatus, saveError, dispatch]);

  // Handle visibility toggle success/failure
  useEffect(() => {
    if (visibilityStatus === "success") {
      setShowHideFromReportsModal(false);
      if (isHiddenFromReports) {
        setToastMessage("Disclaimer section hidden.");
      } else {
        setToastMessage("Disclaimer will be shown in reports.");
      }
      setShowSuccessToast(true);
      dispatch(clearVisibilityStatus());
    } else if (visibilityStatus === "failed") {
      setToastMessage(
        visibilityError || "Failed to update disclaimer visibility"
      );
      setShowErrorToast(true);
      dispatch(clearVisibilityStatus());
    }
  }, [visibilityStatus, visibilityError, isHiddenFromReports, dispatch]);

  // Check if content has been modified
  const isDirty = useCallback(() => {
    return editContentRef.current !== originalContentRef.current;
  }, []);

  // Check if content is empty (only whitespace counts as empty)
  const isContentEmpty = useCallback((text) => {
    return !text || text.trim() === "";
  }, []);

  // Initialize edit content when entering edit mode
  const handleEditClick = () => {
    setEditContent(content);
    editContentRef.current = content;
    originalContentRef.current = content;
    isEditModeRef.current = true;
    setIsEditMode(true);
  };

  // Handle save
  const handleSave = () => {
    if (isContentEmpty(editContent)) {
      setShowEmptyContentModal(true);
      return;
    }
    dispatch(saveDisclaimer(editContent));
  };

  // Handle cancel - check for unsaved changes
  const handleCancel = () => {
    if (isDirty()) {
      setPendingNavigation("cancel");
      setShowDiscardChangesModal(true);
    } else {
      exitEditMode();
    }
  };

  // Exit edit mode without saving
  const exitEditMode = () => {
    setIsEditMode(false);
    setEditContent("");
    isEditModeRef.current = false;
    editContentRef.current = "";
    originalContentRef.current = "";
  };

  // Handle confirming discard changes
  const handleConfirmDiscard = () => {
    setShowDiscardChangesModal(false);
    exitEditMode();
    if (pendingNavigation && pendingNavigation !== "cancel") {
      navigate(pendingNavigation);
    }
    setPendingNavigation(null);
  };

  // Update editContentRef when editContent changes
  useEffect(() => {
    editContentRef.current = editContent;
  }, [editContent]);

  // Disable page scroll when in edit mode
  useEffect(() => {
    if (isEditMode) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isEditMode]);

  // Handle browser back button and sidebar navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (
        isEditModeRef.current &&
        editContentRef.current !== originalContentRef.current
      ) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Intercept link clicks for navigation blocking
  useEffect(() => {
    const handleClick = (e) => {
      if (!isEditModeRef.current) return;
      if (editContentRef.current === originalContentRef.current) return;

      // Find if click was on a link or inside a link
      const link = e.target.closest("a[href]");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("http")) return;

      // Prevent navigation and show modal
      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
      setShowDiscardChangesModal(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  // Handle hide from reports button click - show modal
  const handleHideFromReportsClick = () => {
    setShowHideFromReportsModal(true);
  };

  // Handle confirm hide from modal
  const handleConfirmHide = () => {
    dispatch(toggleDisclaimerVisibility(true));
  };

  // Handle show in reports button click - directly show
  const handleShowInReports = () => {
    dispatch(toggleDisclaimerVisibility(false));
  };

  // Custom underline command (not supported natively in markdown)
  const underlineCommand = {
    name: "underline",
    keyCommand: "underline",
    shortcuts: "ctrlcmd+u",
    buttonProps: { "aria-label": "Add underline text" },
    icon: (
      <span
        style={{
          textDecoration: "underline",
          position: "relative",
          bottom: "1px",
        }}
      >
        U
      </span>
    ),
    execute: (state, api) => {
      const newText = state.selectedText
        ? `<u>${state.selectedText}</u>`
        : "<u>underlined text</u>";
      api.replaceSelection(newText);
    },
  };

  // Toolbar commands - using library's built-in commands
  const editorCommands = [
    commands.bold,
    commands.italic,
    underlineCommand,
    commands.orderedListCommand,
    commands.unorderedListCommand,
  ];

  const isPageLoading = fetchStatus === "loading";

  if (fetchStatus === "failed") {
    return (
      <div className="disclaimer-settings-container">
        <div className="error-state">
          <p>Failed to load disclaimer: {fetchError}</p>
          <Button variant="primary" onClick={() => dispatch(fetchDisclaimer())}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      customHeader={
        <div className="disclaimer-settings-header">
          <div className="header-title-row">
            <h2>Disclaimer Setting</h2>
            {/* Mobile: Icon-only How It Works button next to title */}
            {!isEditMode && (
              <button
                className="how-it-works-icon-btn"
                onClick={() => setShowHowItWorksModal(true)}
                aria-label="How It Works"
              >
                <Icon path={mdiHelpCircleOutline} size={1} />
              </button>
            )}
          </div>
          <div className="header-actions">
            {/* Desktop: Full How It Works button */}
            {!isEditMode && (
              <Button
                variant="outline-primary"
                className="how-it-works-btn"
                onClick={() => setShowHowItWorksModal(true)}
              >
                <Icon path={mdiHelpCircleOutline} size={0.8} className="me-2" />
                How It Works
              </Button>
            )}
            {isPageLoading ? (
              <Button variant="primary" className="edit-btn" disabled>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              </Button>
            ) : isEditMode ? (
              <>
                <Button
                  variant="outline-secondary"
                  onClick={handleCancel}
                  disabled={saveStatus === "loading"}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saveStatus === "loading"}
                >
                  {saveStatus === "loading" ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </>
            ) : isHiddenFromReports ? (
              <Button
                variant="primary"
                className="visibility-btn"
                onClick={handleShowInReports}
                disabled={visibilityStatus === "loading"}
              >
                {visibilityStatus === "loading" ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Updating...
                  </>
                ) : (
                  "Show in Reports"
                )}
              </Button>
            ) : (
              <Button
                variant="primary"
                className="edit-btn"
                onClick={handleEditClick}
              >
                <Icon path={mdiPencilOutline} size={0.8} className="me-2" />
                Edit
              </Button>
            )}
          </div>
        </div>
      }
      className={`disclaimer-settings-container ${isEditMode ? "edit-mode" : ""}`}
    >
      {/* Content */}
      <div
        className="disclaimer-content-wrapper"
        style={{ position: "relative", minHeight: "calc(100vh - 200px)" }}
      >
        {isPageLoading && <PartialLoadingSpinner />}
        {!isPageLoading && (
          <>
            {/* Status indicator bar - hidden in edit mode */}
            {!isEditMode && (
              <div
                className={`disclaimer-status-bar ${isHiddenFromReports ? "hidden-status" : "visible-status"}`}
              >
                <div className="status-info">
                  <span
                    className={`status-dot ${isHiddenFromReports ? "gray" : "green"}`}
                  ></span>
                  <span className="status-text">
                    {isHiddenFromReports
                      ? "This section is currently hidden from reports. Click 'Show in Reports' to include it."
                      : "This section will be included in all newly compiled reports."}
                  </span>
                </div>
                {!isHiddenFromReports && (
                  <Button
                    variant="outline-secondary"
                    className="hide-from-reports-btn"
                    onClick={handleHideFromReportsClick}
                  >
                    Hide from Reports
                  </Button>
                )}
              </div>
            )}

            {isEditMode ? (
              <div
                ref={editorWrapperRef}
                className="editor-wrapper"
                data-color-mode="light"
              >
                <MDEditor
                  value={editContent}
                  onChange={setEditContent}
                  preview={isMobile ? "edit" : "live"}
                  height="calc(100vh - 64px - 48px - 88px - 24px)"
                  commands={editorCommands}
                  extraCommands={[]}
                  highlightEnable={false}
                  textareaProps={{
                    placeholder: "Enter your disclaimer content here...",
                  }}
                />
              </div>
            ) : (
              <div
                className={`disclaimer-content ${isHiddenFromReports ? "content-hidden" : ""}`}
              >
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {content || "No disclaimer content set."}
                </ReactMarkdown>
              </div>
            )}
          </>
        )}
      </div>

      {/* How It Works Modal */}
      <DisclaimerHowItWorksModal
        show={showHowItWorksModal}
        onHide={() => setShowHowItWorksModal(false)}
      />

      {/* Hide from Reports Confirmation Modal */}
      <HideFromReportsModal
        show={showHideFromReportsModal}
        onHide={() => setShowHideFromReportsModal(false)}
        onConfirm={handleConfirmHide}
        isLoading={visibilityStatus === "loading"}
      />

      {/* Discard Changes Confirmation Modal */}
      <DiscardChangesModal
        show={showDiscardChangesModal}
        onHide={() => {
          setShowDiscardChangesModal(false);
          setPendingNavigation(null);
        }}
        onConfirm={handleConfirmDiscard}
      />

      {/* Empty Content Modal */}
      <EmptyContentModal
        show={showEmptyContentModal}
        onHide={() => setShowEmptyContentModal(false)}
      />

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={toastMessage}
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
        delay={4000}
      />

      {/* Error Toast */}
      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={toastMessage}
        variant="error"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
        delay={4000}
      />
    </PageLayout>
  );
};

export default DisclaimerSettings;
